import { useEffect, useState } from "react";
import { useAdmin } from "../../context/AdminContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
const token = () => localStorage.getItem("adminToken");

const STATUSES = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled"];

const STATUS_COLORS = {
  pending:    "bg-yellow-100 text-yellow-700",
  confirmed:  "bg-blue-100 text-blue-700",
  processing: "bg-purple-100 text-purple-700",
  shipped:    "bg-indigo-100 text-indigo-700",
  delivered:  "bg-green-100 text-green-700",
  cancelled:  "bg-red-100 text-red-700",
};

const PAYMENT_COLORS = {
  paid:     "bg-green-100 text-green-700",
  pending:  "bg-yellow-100 text-yellow-700",
  failed:   "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-600",
};

const RETURN_COLORS = {
  none:             "",
  requested:        "bg-amber-100 text-amber-700",
  approved:         "bg-blue-100 text-blue-700",
  pickup_scheduled: "bg-violet-100 text-violet-700",
  picked_up:        "bg-orange-100 text-orange-700",
  refund_initiated: "bg-rose-100 text-rose-700",
  completed:        "bg-green-100 text-green-700",
  rejected:         "bg-red-100 text-red-700",
};

// Return flow steps — admin can move forward through these
const RETURN_FLOW = [
  { key: "requested",        label: "Requested",        emoji: "📋" },
  { key: "approved",         label: "Approved",         emoji: "✅" },
  { key: "pickup_scheduled", label: "Pickup Scheduled", emoji: "📅" },
  { key: "picked_up",        label: "Picked Up",        emoji: "📦" },
  { key: "refund_initiated", label: "Refund Initiated", emoji: "💳" },
  { key: "completed",        label: "Completed",        emoji: "🎉" },
];

function StatusBadge({ status, map }) {
  if (!status || !map[status]) return null;
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${map[status]}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

// ── Return Tracker (admin view) ───────────────────────────────
function ReturnTracker({ order, onReturnUpdated }) {
  const [returnStatus, setReturnStatus] = useState(order.return_status || "none");
  const [pickupDate,   setPickupDate]   = useState(order.return_pickup_date || "");
  const [notes,        setNotes]        = useState(order.return_notes || "");
  const [saving,       setSaving]       = useState(false);

  const activeIdx = RETURN_FLOW.findIndex(s => s.key === returnStatus);

  async function updateReturnStatus(newStatus) {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/orders/${order.id}/return-status`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify({
          return_status:      newStatus,
          return_pickup_date: pickupDate || null,
          return_notes:       notes || null,
        }),
      });
      if (!res.ok) throw new Error("Failed to update");
      setReturnStatus(newStatus);
      onReturnUpdated(order.id, { return_status: newStatus, return_pickup_date: pickupDate, return_notes: notes });
    } catch {
      alert("Failed to update return status.");
    } finally {
      setSaving(false);
    }
  }

  async function handleReject() {
    if (!window.confirm("Reject this return request?")) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/orders/${order.id}/return-status`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify({ return_status: "rejected", return_notes: notes || null }),
      });
      if (!res.ok) throw new Error("Failed to update");
      setReturnStatus("rejected");
      onReturnUpdated(order.id, { return_status: "rejected", return_notes: notes });
    } catch {
      alert("Failed to reject return.");
    } finally {
      setSaving(false);
    }
  }

  if (returnStatus === "none") return null;

  const isRejected  = returnStatus === "rejected";
  const isCompleted = returnStatus === "completed";
  const nextStep    = !isRejected && !isCompleted ? RETURN_FLOW[activeIdx + 1] : null;

  return (
    <div className="bg-gray-50 rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-800">🔄 Return Request</p>
        <StatusBadge status={returnStatus} map={RETURN_COLORS} />
      </div>

      {/* Customer reason */}
      {order.return_reason && (
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Customer Reason</p>
          <p className="text-sm text-gray-700">{order.return_reason}</p>
          {order.return_requested_at && (
            <p className="text-xs text-gray-400 mt-1">
              Requested: {new Date(order.return_requested_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
      {/* Requested items */}
      {order.return_items && order.return_items.length > 0 && (
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
            Items Requested
          </p>
          <div className="space-y-2">
            {order.return_items.map(ri => (
              <div key={ri.id} className="flex items-center gap-3 py-1.5 border-b border-gray-100 last:border-0">
                {ri.image && (
                  <img src={ri.image} alt={ri.name} className="w-8 h-8 rounded-md object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{ri.name}</p>
                  <p className="text-xs text-gray-400">
                    Qty: {ri.qty}{ri.size ? ` · Size ${ri.size}` : ""}{ri.color ? ` · ${ri.color}` : ""}
                  </p>
                </div>
                <span
                  className={`text-[11px] font-bold px-2 py-1 rounded-full capitalize ${
                    ri.return_type === "exchange"
                      ? "text-blue-600 bg-blue-50"
                      : "text-emerald-600 bg-emerald-50"
                  }`}
                >
                  {ri.return_type}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Return progress bar */}
      {!isRejected && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Progress</p>
          <div className="flex items-center gap-1">
            {RETURN_FLOW.map((step, idx) => {
              const done    = idx <= activeIdx;
              const current = idx === activeIdx;
              return (
                <div key={step.key} className="flex items-center flex-1">
                  <div className={`flex flex-col items-center gap-1 flex-1`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs border-2 transition-all
                      ${done ? "bg-rose-500 border-rose-500 text-white" : "bg-white border-gray-200 text-gray-300"}
                      ${current ? "ring-2 ring-rose-200" : ""}`}>
                      {done ? "✓" : idx + 1}
                    </div>
                    <span className={`text-[9px] font-semibold text-center leading-tight
                      ${current ? "text-rose-600" : done ? "text-gray-600" : "text-gray-300"}`}>
                      {step.label.split(" ")[0]}
                    </span>
                  </div>
                  {idx < RETURN_FLOW.length - 1 && (
                    <div className={`h-0.5 flex-1 mb-4 ${idx < activeIdx ? "bg-rose-400" : "bg-gray-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pickup date (show when approving or already scheduled) */}
      {!isRejected && !isCompleted && ["requested", "approved", "pickup_scheduled"].includes(returnStatus) && (
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5 block">
            Pickup Date {returnStatus === "pickup_scheduled" ? "(scheduled)" : "(optional)"}
          </label>
          <input type="date" value={pickupDate} onChange={e => setPickupDate(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400" />
        </div>
      )}

      {/* Notes */}
      {!isCompleted && (
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5 block">
            Notes for Customer (optional)
          </label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="e.g. Our team will contact you for pickup..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400 resize-none" />
        </div>
      )}

      {/* Action buttons */}
      {!isRejected && !isCompleted && (
        <div className="flex gap-2">
          {/* Move to next step */}
          {nextStep && (
            <button onClick={() => updateReturnStatus(nextStep.key)} disabled={saving}
              className="flex-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg text-sm font-bold transition">
              {saving ? "Saving..." : `${nextStep.emoji} Mark as ${nextStep.label}`}
            </button>
          )}
          {/* Reject (only when requested or approved) */}
          {["requested", "approved"].includes(returnStatus) && (
            <button onClick={handleReject} disabled={saving}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold border border-red-200 text-red-500 hover:bg-red-50 transition disabled:opacity-50">
              Reject
            </button>
          )}
        </div>
      )}

      {isRejected && (
        <div className="bg-red-50 rounded-lg px-4 py-3 text-sm text-red-600 font-semibold">
          ❌ This return has been rejected.
        </div>
      )}
      {isCompleted && (
        <div className="bg-green-50 rounded-lg px-4 py-3 text-sm text-green-600 font-semibold">
          ✅ Return completed successfully.
        </div>
      )}
    </div>
  );
}

// ── Order Detail Drawer ───────────────────────────────────────
function OrderDrawer({ order, onClose, onStatusChange, onReturnUpdated }) {
  const [status, setStatus] = useState(order.status);
  const [saving, setSaving] = useState(false);

  async function handleStatusSave() {
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/orders/${order.id}/status`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify({ status }),
      });
      onStatusChange(order.id, status);
    } catch {
      alert("Failed to update status.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end">
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Order #{order.id}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{new Date(order.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-6">

          {/* ── Return Request (show at top if pending) ── */}
          {order.return_status && order.return_status !== "none" && (
            <ReturnTracker order={order} onReturnUpdated={onReturnUpdated} />
          )}

          {/* Order Status */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Update Order Status</p>
            <div className="flex gap-2">
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
                {STATUSES.map(s => (
                  <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <button onClick={handleStatusSave} disabled={saving || status === order.status}
                className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold transition">
                {saving ? "Saving..." : "Update"}
              </button>
            </div>
          </div>

          {/* Customer Info */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Customer</p>
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-1">
              <p className="text-sm font-semibold text-gray-900">{order.customer_name}</p>
              <p className="text-sm text-gray-500">{order.customer_email}</p>
            </div>
          </div>

          {/* Delivery Address */}
          {order.address_line1 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Delivery Address</p>
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-1">
                <p className="text-sm font-semibold text-gray-900">{order.full_name}</p>
                <p className="text-sm text-gray-500">{order.phone}</p>
                <p className="text-sm text-gray-500">{order.address_line1}</p>
                {order.address_line2 && <p className="text-sm text-gray-500">{order.address_line2}</p>}
                <p className="text-sm text-gray-500">{order.city}, {order.state} {order.zip}</p>
                <p className="text-sm text-gray-500">{order.country}</p>
              </div>
            </div>
          )}

          {/* Order Items */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Items</p>
            <div className="space-y-3">
              {order.items.map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3">
                  {item.image && (
                    <img src={item.image} alt={item.name}
                      className="w-14 h-14 object-cover rounded-lg border border-gray-100" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {[item.size && `Size: ${item.size}`, item.color && `Color: ${item.color}`].filter(Boolean).join(" · ")}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Qty: {item.qty}</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900">${Number(item.price * item.qty).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Summary</p>
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span><span>${Number(order.subtotal).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Delivery</span><span>${Number(order.delivery_charge || 0).toLocaleString()}</span>
              </div>
              {order.reward_discount > 0 && (
                <div className="flex justify-between text-sm text-amber-600">
                  <span>🪙 Reward Discount</span><span>-${Number(order.reward_discount).toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold text-gray-900 border-t border-gray-100 pt-2">
                <span>Total</span><span>${Number(order.total).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500 pt-1">
                <span>Payment</span>
                <StatusBadge status={order.payment_status} map={PAYMENT_COLORS} />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Main Orders Page ──────────────────────────────────────────
export default function Orders() {
  const [orders,       setOrders]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [returnFilter, setReturnFilter] = useState("");
  const [selected,     setSelected]     = useState(null);

  const { admin }   = useAdmin();
  const isAdmin     = admin?.role === "admin";

  useEffect(() => { fetchOrders(); }, []);

  async function fetchOrders() {
    try {
      const res  = await fetch(`${API}/api/admin/orders`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setOrders(data);
    } catch {
      setError("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  }

  function onStatusChange(id, newStatus) {
    setOrders(os => os.map(o => o.id === id ? { ...o, status: newStatus } : o));
    if (selected?.id === id) setSelected(s => ({ ...s, status: newStatus }));
  }

  function onReturnUpdated(id, returnData) {
    setOrders(os => os.map(o => o.id === id ? { ...o, ...returnData } : o));
    if (selected?.id === id) setSelected(s => ({ ...s, ...returnData }));
  }

  const pendingReturns = orders.filter(o => o.return_status === "requested").length;

  const filtered = orders.filter(o => {
    const matchSearch = o.customer_name?.toLowerCase().includes(search.toLowerCase())
                     || String(o.id).includes(search);
    const matchStatus = statusFilter ? o.status === statusFilter : true;
    const matchReturn = returnFilter ? o.return_status === returnFilter : true;
    return matchSearch && matchStatus && matchReturn;
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-400 text-sm animate-pulse">Loading orders...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-screen-xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-500 text-sm mt-1">{orders.length} total orders</p>
        </div>
        {/* Pending returns alert */}
        {pendingReturns > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer"
            onClick={() => setReturnFilter("requested")}>
            🔄 {pendingReturns} return{pendingReturns > 1 ? "s" : ""} pending review
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by customer or order ID..."
          className="flex-1 min-w-48 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-rose-400"
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
          <option value="">All Statuses</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select value={returnFilter} onChange={e => setReturnFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
          <option value="">All Returns</option>
          <option value="requested">Return Requested</option>
          <option value="approved">Return Approved</option>
          <option value="pickup_scheduled">Pickup Scheduled</option>
          <option value="picked_up">Picked Up</option>
          <option value="refund_initiated">Refund Initiated</option>
          <option value="completed">Return Completed</option>
          <option value="rejected">Return Rejected</option>
        </select>
      </div>

      {/* Status Pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        {STATUSES.map(s => {
          const count = orders.filter(o => o.status === s).length;
          return (
            <button key={s} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition border
                ${statusFilter === s
                  ? STATUS_COLORS[s] + " border-transparent"
                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)} ({count})
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-5 py-3 text-left">Order ID</th>
                <th className="px-5 py-3 text-left">Customer</th>
                <th className="px-5 py-3 text-left">Items</th>
                <th className="px-5 py-3 text-left">Total</th>
                <th className="px-5 py-3 text-left">Payment</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Return</th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(o => (
                <tr key={o.id} className={`hover:bg-gray-50 transition ${o.return_status === "requested" ? "bg-amber-50/30" : ""}`}>
                  <td className="px-5 py-3 font-mono text-gray-600">#{o.id}</td>
                  <td className="px-5 py-3">
                    <p className="font-medium text-gray-900">{o.customer_name}</p>
                    <p className="text-xs text-gray-400">{o.customer_email}</p>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{o.items?.length || 0} item(s)</td>
                  <td className="px-5 py-3 font-semibold text-gray-900">${Number(o.total).toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={o.payment_status} map={PAYMENT_COLORS} />
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={o.status} map={STATUS_COLORS} />
                  </td>
                  <td className="px-5 py-3">
                    {o.return_status && o.return_status !== "none"
                      ? <StatusBadge status={o.return_status} map={RETURN_COLORS} />
                      : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-5 py-3 text-gray-400 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => setSelected(o)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded hover:bg-blue-50 transition">
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-gray-400">No orders found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <OrderDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onStatusChange={onStatusChange}
          onReturnUpdated={onReturnUpdated}
        />
      )}
    </div>
  );
}